import AssignmentTurnedInTwoToneIcon from '@mui/icons-material/AssignmentTurnedInTwoTone';
import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import DesignServicesTwoToneIcon from '@mui/icons-material/DesignServicesTwoTone';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupAddTwoToneIcon from '@mui/icons-material/GroupAddTwoTone';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PendingActionsTwoToneIcon from '@mui/icons-material/PendingActionsTwoTone';
import PeopleAltTwoToneIcon from '@mui/icons-material/PeopleAltTwoTone';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import TrainIcon from '@mui/icons-material/Train';
import {
  alpha,
  Box,
  Button,
  List,
  ListItem,
  ListSubheader,
  styled
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { useContext } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from 'src/contexts/AuthContext';
import { SidebarContext } from 'src/contexts/SidebarContext';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Protection } from 'src/components/Protection';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: #fff;
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: #fff;
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: #fff;
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
              color: #fff;
            }
          }

          .MuiButton-startIcon {
            color: #fff;
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: #fff;
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: #fff;

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: #fff;
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: #fff;
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  // Role checking is now handled by Protection components
  // All manual role checks have been replaced with Protection components

  return (
    <>
      <MenuWrapper>
        {/* Dashboard Section */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              {t('sidebar.dashboard')}
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/main"
                  startIcon={<DesignServicesTwoToneIcon />}
                  className={
                    location.pathname.startsWith('/dashboards/main')
                      ? 'active'
                      : ''
                  }
                  aria-current={
                    location.pathname.startsWith('/dashboards/main')
                      ? 'page'
                      : undefined
                  }
                >
                  {t('sidebar.overview')}
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {/* Operations Section - hide for ACCOUNTING role */}
        <Protection mode="content" blockedRoles={['ACCOUNTING']}>
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.operations')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                {/* Requests & Orders */}
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/requests"
                    startIcon={<ListAltIcon />}
                    className={
                      location.pathname.startsWith('/requests') ? 'active' : ''
                    }
                    aria-current={
                      location.pathname.startsWith('/requests')
                        ? 'page'
                        : undefined
                    }
                  >
                    {t('sidebar.requests_orders')}
                  </Button>
                </ListItem>
                {/* Disposition Calendar - Admin/Dispatcher Only */}
                <Protection
                  mode="content"
                  allowedRoles={['ADMIN', 'DISPATCHER']}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/disposition/calendar"
                      startIcon={<CalendarMonthTwoToneIcon />}
                      className={
                        location.pathname.startsWith('/disposition/calendar')
                          ? 'active'
                          : ''
                      }
                      aria-current={
                        location.pathname.startsWith('/disposition/calendar')
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.disposition_calendar')}
                    </Button>
                  </ListItem>
                </Protection>
                {/* Track Views - Dispatcher Only */}
                <Protection
                  mode="content"
                  allowedRoles={['ADMIN', 'DISPATCHER']}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/disposition/tracks"
                      startIcon={<ListAltIcon />}
                      className={
                        location.pathname.startsWith('/disposition/tracks')
                          ? 'active'
                          : ''
                      }
                      aria-current={
                        location.pathname.startsWith('/disposition/tracks')
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.tracks')}
                    </Button>
                  </ListItem>
                </Protection>
                {/* Train List - moved above Reports */}
                <Protection
                  mode="content"
                  allowedRoles={['ADMIN', 'DISPATCHER']}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/trains"
                      startIcon={<TrainIcon />}
                      className={
                        location.pathname.startsWith('/trains') ? 'active' : ''
                      }
                      aria-current={
                        location.pathname.startsWith('/trains')
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.train_list')}
                    </Button>
                  </ListItem>
                </Protection>
                {/* Reports - moved from Accounting to Operations, visible to Admin/Dispatcher */}
                <Protection
                  mode="content"
                  allowedRoles={['ADMIN', 'DISPATCHER']}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/reports"
                      startIcon={<AssessmentIcon />}
                    >
                      {t('sidebar.reports', 'Reports')}
                    </Button>
                  </ListItem>
                </Protection>
                {/* Simulate Train Arrival/Departure - Admin Only */}
                <Protection mode="content" allowedRoles={['ADMIN']}>
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/operations/simulate-train-ops"
                      startIcon={<PlayArrowIcon />}
                      className={
                        location.pathname.startsWith(
                          '/operations/simulate-train-ops'
                        )
                          ? 'active'
                          : ''
                      }
                      aria-current={
                        location.pathname.startsWith(
                          '/operations/simulate-train-ops'
                        )
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.simulate_train_ops')}
                    </Button>
                  </ListItem>
                </Protection>
              </List>
            </SubMenuWrapper>
          </List>
        </Protection>

        {/* Infrastructure Section - Admin only */}
        <Protection mode="content" allowedRoles={['ADMIN']}>
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.infrastructure')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/infrastructure/manage"
                    startIcon={<SettingsTwoToneIcon />}
                    className={
                      location.pathname.startsWith('/infrastructure/manage')
                        ? 'active'
                        : ''
                    }
                    aria-current={
                      location.pathname.startsWith('/infrastructure/manage')
                        ? 'page'
                        : undefined
                    }
                  >
                    {t('sidebar.manage')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        </Protection>

        {/* Hide Accounting section for DISPATCHER and RTU role */}
        <Protection mode="content" blockedRoles={['DISPATCHER', 'RTU']}>
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.accounting')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <AccountingBillingInvoicingMenu closeSidebar={closeSidebar} />
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/billing-invoicing/review-tasks"
                    startIcon={<AssignmentTurnedInTwoToneIcon />}
                    className={
                      location.pathname.startsWith(
                        '/billing-invoicing/review-tasks'
                      )
                        ? 'active'
                        : ''
                    }
                    aria-current={
                      location.pathname.startsWith(
                        '/billing-invoicing/review-tasks'
                      )
                        ? 'page'
                        : undefined
                    }
                  >
                    {t('sidebar.requires_review', 'Review Tasks')}
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        </Protection>

        {/* Administration Section */}
        <Protection
          mode="content"
          allowedRoles={['ADMIN', 'SYSTEM_ADMIN', 'EIU_ADMIN', 'EVU_ADMIN']}
        >
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                {t('sidebar.administration')}
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <Protection
                  mode="content"
                  allowedRoles={['SYSTEM_ADMIN', 'EIU_ADMIN', 'EVU_ADMIN']}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/admin/registrations/pending"
                      startIcon={<PendingActionsTwoToneIcon />}
                      className={
                        location.pathname.startsWith(
                          '/admin/registrations/pending'
                        )
                          ? 'active'
                          : ''
                      }
                      aria-current={
                        location.pathname.startsWith(
                          '/admin/registrations/pending'
                        )
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.pending_registrations')}
                    </Button>
                  </ListItem>
                </Protection>
                <Protection
                  mode="content"
                  allowedRoles={['ADMIN', 'EVU_ADMIN', 'EIU_ADMIN']}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/admin/invitations"
                      startIcon={<GroupAddTwoToneIcon />}
                      className={
                        location.pathname.startsWith('/admin/invitations')
                          ? 'active'
                          : ''
                      }
                      aria-current={
                        location.pathname.startsWith('/admin/invitations')
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.user_invitations')}
                    </Button>
                  </ListItem>
                </Protection>
                <Protection
                  mode="content"
                  allowedRoles={[
                    'ADMIN',
                    'SYSTEM_ADMIN',
                    'EIU_ADMIN',
                    'EVU_ADMIN'
                  ]}
                >
                  <ListItem component="div">
                    <Button
                      disableRipple
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/admin/users"
                      startIcon={<PeopleAltTwoToneIcon />}
                      className={
                        location.pathname.startsWith('/admin/users')
                          ? 'active'
                          : ''
                      }
                      aria-current={
                        location.pathname.startsWith('/admin/users')
                          ? 'page'
                          : undefined
                      }
                    >
                      {t('sidebar.user_management')}
                    </Button>
                  </ListItem>
                </Protection>
              </List>
            </SubMenuWrapper>
          </List>
        </Protection>
      </MenuWrapper>
    </>
  );
}

function AccountingBillingInvoicingMenu({ closeSidebar }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const handleExpandClick = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };
  return (
    <>
      <ListItem
        component="div"
        disablePadding
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Button
          disableRipple
          component={RouterLink}
          onClick={closeSidebar}
          to="/billing-invoicing"
          startIcon={<AssignmentTurnedInTwoToneIcon />}
          sx={{ justifyContent: 'flex-start', width: '100%', flex: 1 }}
          className={
            location.pathname.startsWith('/billing-invoicing') ? 'active' : ''
          }
          aria-current={
            location.pathname.startsWith('/billing-invoicing')
              ? 'page'
              : undefined
          }
        >
          {t('sidebar.billing_invoicing')}
        </Button>
        <Box
          onClick={handleExpandClick}
          sx={{
            cursor: 'pointer',
            pr: 2,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {open ? <ExpandLess /> : <ExpandMore />}
        </Box>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 4 }}>
          {/* Billing Items hidden for now */}
          {/* <ListItem component="div">
            <Button
              disableRipple
              component={RouterLink}
              onClick={closeSidebar}
              to="/billing-items"
            >
              {t('sidebar.billing_items')}
            </Button>
          </ListItem> */}
          <ListItem component="div">
            <Button
              disableRipple
              component={RouterLink}
              onClick={closeSidebar}
              to="/tariffs"
              className={
                location.pathname.startsWith('/tariffs') &&
                !location.pathname.startsWith(
                  '/tariffs/service-type-matching-rules'
                )
                  ? 'active'
                  : ''
              }
              aria-current={
                location.pathname.startsWith('/tariffs') &&
                !location.pathname.startsWith(
                  '/tariffs/service-type-matching-rules'
                )
                  ? 'page'
                  : undefined
              }
            >
              {t('sidebar.tariffs_pricing')}
            </Button>
          </ListItem>
          <ListItem component="div">
            <Button
              disableRipple
              component={RouterLink}
              onClick={closeSidebar}
              to="/tariffs/service-type-matching-rules"
              className={
                location.pathname.startsWith(
                  '/tariffs/service-type-matching-rules'
                )
                  ? 'active'
                  : ''
              }
              aria-current={
                location.pathname.startsWith(
                  '/tariffs/service-type-matching-rules'
                )
                  ? 'page'
                  : undefined
              }
            >
              {t('sidebar.service_type_matching_rules')}
            </Button>
          </ListItem>
        </List>
      </Collapse>
    </>
  );
}

export default SidebarMenu;
